// packages/shared/src/map/nav.ts
import { getEntry, tuning, type CatalogId, type ObjectKind, type Rot } from '@whiskerton/content';
import type { TileXY, Vec2 } from '../types/geom';
import type { MapData, NavGrid } from '../types/map';
import { groundAt, rampDirAt, tierAt } from './codec';

export type { NavGrid } from '../types/map';
/** Any map with tiles; MapData satisfies it (the generator passes its work-in-progress grid). */
type Tiles = Pick<MapData, 'w' | 'h' | 'tiles'>;
type Obj = { x: number; y: number; catalogId: CatalogId; rot: Rot; kind: ObjectKind };
export const DIRS = [[0, -1], [1, 0], [0, 1], [-1, 0]] as const; // N E S W = ramp dirs 0..3
const WALK_GROUND = new Set(['grass', 'sand', 'path', 'ramp']);
/** Tiles covered by a footprint anchored at (x, y) (top-left of the rotated footprint). */
export function footprintTiles(o: { x: number; y: number; rot: number }, fp: readonly [number, number]): [number, number][] {
  const [w, d] = o.rot % 2 === 0 ? fp : [fp[1], fp[0]];
  const out: [number, number][] = [];
  for (let dy = 0; dy < d; dy++) for (let dx = 0; dx < w; dx++) out.push([o.x + dx, o.y + dy]);
  return out;
}
export function buildNavGrid(map: Tiles, objects: readonly Obj[]): NavGrid {
  const { w, h } = map;
  const walk = new Uint8Array(w * h), tier = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    walk[i] = WALK_GROUND.has(groundAt(map, x, y)) ? 1 : 0;
    tier[i] = tierAt(map, x, y);
  }
  for (const o of objects) {
    const e = getEntry(o.catalogId);
    if (!e || e.kind !== 'world') continue;
    for (const [x, y] of footprintTiles(o, e.footprint)) {
      if (x < 0 || y < 0 || x >= w || y >= h) continue;
      if (o.kind === 'bridge') walk[y * w + x] = 1;
      else if (e.blocks) walk[y * w + x] = 0;
    }
  }
  return { w, h, walk, tier };
}
function tierStepOk(map: Tiles, fx: number, fy: number, tx: number, ty: number, tf: number, tt: number): boolean {
  if (tf === tt) return true;
  if (Math.abs(tf - tt) !== 1 || (fx !== tx && fy !== ty)) return false;
  const dx = tx - fx, dy = ty - fy;
  const dirFromTo = DIRS.findIndex(([a, b]) => a === dx && b === dy);
  if (tt === tf + 1) return rampDirAt(map, fx, fy) === dirFromTo;
  return rampDirAt(map, tx, ty) === (dirFromTo + 2) % 4;
}
/** 8 directions, no corner cutting; a tier change only orthogonally, up or down a ramp's ascent direction. */
export function canStep(grid: NavGrid, map: Tiles, from: TileXY, to: TileXY): boolean {
  const dx = to.x - from.x, dy = to.y - from.y;
  if ((dx === 0 && dy === 0) || Math.abs(dx) > 1 || Math.abs(dy) > 1) return false;
  if (to.x < 0 || to.y < 0 || to.x >= grid.w || to.y >= grid.h) return false;
  const ti = to.y * grid.w + to.x, fi = from.y * grid.w + from.x;
  if (!grid.walk[ti]) return false;
  const tf = grid.tier[fi]!, tt = grid.tier[ti]!;
  if (dx !== 0 && dy !== 0) {
    const a = from.y * grid.w + to.x, b = to.y * grid.w + from.x;
    if (!grid.walk[a] || !grid.walk[b]) return false;
    return tf === tt && grid.tier[a] === tf && grid.tier[b] === tf;
  }
  return tierStepOk(map, from.x, from.y, to.x, to.y, tf, tt);
}
export function isWalkablePos(grid: NavGrid, p: Vec2): boolean {
  const x = Math.floor(p.x), y = Math.floor(p.y);
  return x >= 0 && y >= 0 && x < grid.w && y < grid.h && grid.walk[y * grid.w + x] === 1;
}
/** Ground height in world units: tier × tierRise; ramps rise linearly along their ascent direction. */
export function heightAt(map: Tiles, p: Vec2): number {
  const x = Math.floor(p.x), y = Math.floor(p.y);
  const base = tierAt(map, x, y);
  const dir = rampDirAt(map, x, y);
  let frac = 0;
  if (dir !== null) {
    const fx = Math.min(1, Math.max(0, p.x - x)), fy = Math.min(1, Math.max(0, p.y - y));
    frac = dir === 0 ? 1 - fy : dir === 1 ? fx : dir === 2 ? fy : 1 - fx;
  }
  return (base + frac) * tuning.map.tierRise;
}
/** A* over 8 directions. Returns [from, …, to] (to = a tile next to `to` when adjacentOk and `to` is blocked), or null. */
export function findPath(grid: NavGrid, map: Tiles, from: TileXY, to: TileXY,
  opts: { maxNodes?: number; adjacentOk?: boolean } = {}): TileXY[] | null {
  const { w, h } = grid, max = opts.maxNodes ?? 20_000;
  const inB = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h;
  if (!inB(from.x, from.y) || !inB(to.x, to.y)) return null;
  const goal = to.y * w + to.x;
  const goalBlocked = !grid.walk[goal];
  if (goalBlocked && !opts.adjacentOk) return null;
  const isGoal = (i: number) => (goalBlocked ? Math.max(Math.abs((i % w) - to.x), Math.abs(Math.floor(i / w) - to.y)) === 1 : i === goal);
  const g = new Float64Array(w * h).fill(Infinity), came = new Int32Array(w * h).fill(-1), closed = new Uint8Array(w * h);
  const heap: number[] = [], f = new Float64Array(w * h);
  const hfn = (i: number) => { const dx = Math.abs((i % w) - to.x), dy = Math.abs(Math.floor(i / w) - to.y); return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy); };
  const push = (i: number) => { heap.push(i); let c = heap.length - 1; while (c > 0) { const p = (c - 1) >> 1; if (f[heap[p]!]! <= f[i]!) break; heap[c] = heap[p]!; c = p; } heap[c] = i; };
  const pop = () => {
    const top = heap[0]!, last = heap.pop()!;
    if (heap.length > 0) {
      let c = 0;
      const n = heap.length;
      for (;;) {
        const l = 2 * c + 1, r = l + 1;
        let m = c, fm = f[last]!;
        if (l < n && f[heap[l]!]! < fm) { m = l; fm = f[heap[l]!]!; }
        if (r < n && f[heap[r]!]! < fm) m = r;
        if (m === c) break;
        heap[c] = heap[m]!;
        c = m;
      }
      heap[c] = last;
    }
    return top;
  };
  const s = from.y * w + from.x;
  g[s] = 0; f[s] = hfn(s); push(s);
  let expanded = 0;
  while (heap.length) {
    const i = pop();
    if (closed[i]) continue;
    closed[i] = 1;
    if (isGoal(i)) { const out: TileXY[] = []; for (let c = i; c !== -1; c = came[c]!) out.push({ x: c % w, y: Math.floor(c / w) }); return out.reverse(); }
    if (++expanded > max) return null;
    const x = i % w, y = Math.floor(i / w);
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue;
      const nx = x + dx, ny = y + dy;
      if (!canStep(grid, map, { x, y }, { x: nx, y: ny })) continue;
      const j = ny * w + nx, cost = g[i]! + (dx && dy ? Math.SQRT2 : 1);
      if (cost < g[j]!) { g[j] = cost; came[j] = i; f[j] = cost + hfn(j); push(j); }
    }
  }
  return null;
}
